// Page view + funnel visit + click tracking hook
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackFunnelEvent, trackPageview, trackClick } from '../utils/ev';

// Module-level flag — no device storage (ePrivacy compliant)
// Resets on full page reload (new JS context = new session)
let sessionFired = false;

// Derive a human-readable button identifier from the DOM element
const getButtonId = (el) => {
    // Prefer explicit data attribute
    if (el.dataset.trackId) return el.dataset.trackId;
    // Use aria-label
    if (el.ariaLabel) return el.ariaLabel;
    // Use visible text (trim, cap at 50 chars)
    const text = el.innerText?.trim();
    if (text && text.length <= 50) return text;
    if (text) return text.slice(0, 50);
    // Use title
    if (el.title) return el.title;
    // Fallback to tag + class hint
    const cls = el.className?.split?.(' ')?.[0];
    return cls ? `${el.tagName.toLowerCase()}.${cls}` : el.tagName.toLowerCase();
};

const useEv = (userId, userEmail) => {
    const location = useLocation();
    const lastPath = useRef(null);

    // Pageview + funnel tracking
    useEffect(() => {
        const path = location.pathname;

        // Skip duplicate tracking for same path
        if (path === lastPath.current) return;
        lastPath.current = path;

        const isNewSession = !sessionFired;
        if (isNewSession) {
            sessionFired = true;
        }

        // Get referrer (only meaningful on first page load)
        const referrer = document.referrer || '';

        trackFunnelEvent('visit');
        trackPageview({
            path,
            isNewSession,
            userId: userId || null,
            userEmail: userEmail || null,
            referrer: isNewSession ? referrer : null,
        });
    }, [location.pathname, userId, userEmail]);

    // Global click listener — captures all button/a clicks, fire-and-forget
    useEffect(() => {
        const handler = (e) => {
            // Walk up from target to find the nearest button or link
            const el = e.target.closest('button, a, [role="button"]');
            if (!el) return;
            const buttonId = getButtonId(el);
            trackClick(buttonId, location.pathname);
        };
        document.addEventListener('click', handler, { capture: true, passive: true });
        return () => document.removeEventListener('click', handler, { capture: true });
    }, [location.pathname]);
};

export default useEv;
